import { GeneralMinusInitialIcon, GeneralPlusInitialIcon } from '@outdoorsyco/bonfire';
import React, { SVGProps } from 'react';
import { useIntl } from 'react-intl';

import { ZOOM } from '@/constants/map';
import { lastZoomDirection } from '@/redux/selectors/analytics-selectors';

import css from './ZoomControls.module.css';

interface IProps {
  zoom: number;
  onZoom: (relativeZoom: number) => void;
  isDesktopMapViewEnabled?: boolean;
}

type TZoom = 'zoom_in' | 'zoom_out';
interface IControl {
  'aria-label': string;
  'data-testid': string;
  disabled: boolean;
  onClick: () => void;
  Icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
}

const ZoomControls: React.FC<IProps> = ({ zoom, onZoom }) => {
  const intl = useIntl();
  const ZOOM_IN_DISABLED = zoom === ZOOM.MAX;
  const ZOOM_OUT_DISABLED = zoom === ZOOM.MIN;

  const constrolsMap: Record<TZoom, IControl> = {
    zoom_in: {
      'aria-label': intl.formatMessage({
        defaultMessage: 'Zoom in',
        id: 'SAiy57',
        description: 'Search > map > zoom in button',
      }),
      'data-testid': `map-zoom-in-${ZOOM_IN_DISABLED ? 'disabled' : 'enabled'}`,
      disabled: ZOOM_IN_DISABLED,
      Icon: GeneralPlusInitialIcon,
      onClick: () => {
        onZoom(1);
        lastZoomDirection(1);
      },
    },
    zoom_out: {
      'aria-label': intl.formatMessage({
        defaultMessage: 'Zoom out',
        id: 'khV5pE',
        description: 'Search > map > zoom out button',
      }),
      'data-testid': `map-zoom-out-${ZOOM_OUT_DISABLED ? 'disabled' : 'enabled'}`,
      disabled: ZOOM_OUT_DISABLED,
      Icon: GeneralMinusInitialIcon,
      onClick: () => {
        onZoom(-1);
        lastZoomDirection(-1);
      },
    },
  };

  const controlTemplate = (type: TZoom) => {
    const { Icon, ...props } = constrolsMap[type];

    return (
      <button className={`${css.button} !text-750`} {...props}>
        <Icon />
      </button>
    );
  };

  return (
    <div
      className="items-center hidden px-2 bg-white rounded-full lg:inline-flex gap-2"
      data-testid="map-zoom-controls">
      {controlTemplate('zoom_out')}
      <span className="w-px bg-gray-200 h-11" />
      {controlTemplate('zoom_in')}
    </div>
  );
};

export default ZoomControls;
